import { completeInvoiceAmounts } from '../modules/invoices/workflowInvoiceAmounts';
// Normalizers for the invoice workflow editor read model.
//
// Missing stays missing: absent collections map to linesLoaded=false (never an
// empty replacement), and every money/rate/quantity maps to null when absent —
// never zero, never a guessed FX rate, never a default physical unit.
// Permission-redacted absence stays absence. Mirrors dto/pz.ts + dto/wz.ts
// patterns.
//
// Two layers: `normalizeWorkflowInvoice(Line)` for the internal editor model,
// and `normalizePurchaseInvoiceDraft` / `normalizeSalesInvoiceDraft` adapters
// for actual backend reads (PurchaseInvoice + Invoice+InvoiceItem). Server
// identity (id/version) and itemsLoaded survive the adaptation; an unknown
// server status maps to read-only UNKNOWN, never to an editable DRAFT.
import {
  asArray,
  asOptionalArray,
  asOptionalString,
  asRecord,
  reportDtoAnomaly,
} from './common';
import type {
  WorkflowExtractionStatus,
  WorkflowInvoice,
  WorkflowInvoiceDirection,
  WorkflowInvoiceExtractionReview,
  WorkflowInvoiceKind,
  WorkflowInvoiceLine,
  WorkflowInvoiceStatus,
} from '../types/workflowInvoice';
import { normalizeUnitCode } from '../utils/units';

const DIRECTIONS: WorkflowInvoiceDirection[] = ['PURCHASE', 'SALES'];
const KINDS: WorkflowInvoiceKind[] = ['STANDARD', 'CORRECTION', 'ADVANCE', 'PROFORMA', 'UNKNOWN'];
const EDITOR_STATUSES: WorkflowInvoiceStatus[] = [
  'DRAFT',
  'EXTRACTED',
  'READY',
  'APPROVED',
  'CANCELLED',
  'UNKNOWN',
];
const EXTRACTION_STATUSES: WorkflowExtractionStatus[] = ['IDLE', 'PENDING', 'SUCCESS', 'STALE', 'FAILED'];

export function normalizeWorkflowInvoice(raw: unknown, entity = 'workflowInvoice'): WorkflowInvoice {
  const record = asRecord(raw);
  if (!record) {
    reportDtoAnomaly(entity, 'root', raw);
  }

  const lines = asOptionalArray(record?.lines, (item, index) =>
    normalizeWorkflowInvoiceLine(item, `${entity}.lines[${index}]`),
  );

  return {
    id: asOptionalString(record?.id) ?? null,
    version: optionalNullableInteger(record?.version),
    direction: normalizeDirection(record?.direction, `${entity}.direction`),
    kind: normalizeKind(record?.kind, `${entity}.kind`),
    status: normalizeEditorStatus(record?.status, `${entity}.status`),
    extractionStatus: normalizeExtractionStatus(record?.extractionStatus, `${entity}.extractionStatus`),
    extractionError: asOptionalString(record?.extractionError) ?? null,
    extractionModel: asOptionalString(record?.extractionModel) ?? null,
    extractionReview: normalizeExtractionReview(record?.extractionReview, `${entity}.extractionReview`),
    originalDocumentNumber: asOptionalString(record?.originalDocumentNumber) ?? null,
    sellerName: asOptionalString(record?.sellerName) ?? null,
    sellerTaxId: asOptionalString(record?.sellerTaxId) ?? null,
    buyerName: asOptionalString(record?.buyerName) ?? null,
    buyerTaxId: asOptionalString(record?.buyerTaxId) ?? null,
    issueDate: asOptionalString(record?.issueDate) ?? null,
    dueDate: asOptionalString(record?.dueDate) ?? null,
    currency: asOptionalString(record?.currency) ?? null,
    exchangeRateToPLN: optionalNullableNumber(record?.exchangeRateToPLN),
    totalNet: optionalNullableNumber(record?.totalNet),
    totalVat: optionalNullableNumber(record?.totalVat),
    totalGross: optionalNullableNumber(record?.totalGross),
    settlementPeriod: asOptionalString(record?.settlementPeriod) ?? null,
    supplierOrderId: asOptionalString(record?.supplierOrderId) ?? null,
    supplierId: asOptionalString(record?.supplierId) ?? null,
    orderId: asOptionalString(record?.orderId) ?? null,
    correctsInvoiceId: asOptionalString(record?.correctsInvoiceId) ?? null,
    coverageState: normalizeCoverageState(record?.coverageState, `${entity}.coverageState`),
    lines: lines ?? [],
    linesLoaded: lines !== undefined,
    manuallyCorrected: record?.manuallyCorrected === true || record?.manuallyEdited === true,
    approvedAt: asOptionalString(record?.approvedAt) ?? null,
    approvedBy: asOptionalString(record?.approvedBy ?? record?.approvedById) ?? null,
    sourceFileName: asOptionalString(record?.sourceFileName) ?? null,
    approvalResult: normalizeApprovalResult(record?.approvalResult),
    sourceFiles: asArray(record?.sourceFiles, (file) => ({ name: asOptionalString(asRecord(file)?.name) ?? '', sha256: asOptionalString(asRecord(file)?.sha256) ?? '' })),
    extractionSeq: optionalNullableInteger(record?.extractionSeq),
    lastAppliedExtractionSeq: optionalNullableInteger(record?.lastAppliedExtractionSeq),
  };
}

export function normalizeWorkflowInvoices(raw: unknown, entity = 'workflowInvoices'): WorkflowInvoice[] {
  return asArray(raw, (item, index) => normalizeWorkflowInvoice(item, `${entity}[${index}]`));
}

/**
 * Adapter for actual `PurchaseInvoice` backend reads (migration 151 shape:
 * documentNumber/documentKind/status/version/currency/exchangeRate,
 * subtotalNet/vatTotal/grossTotal, sellerSnapshot/buyerSnapshot, sourceFile*,
 * extractionStatus NONE/PENDING/SUCCEEDED/FAILED, manuallyEdited,
 * approvedAt/approvedById, coverageState PARTIAL/FULL, items[]).
 *
 * Identity, version and itemsLoaded are preserved. Unknown server statuses
 * become read-only UNKNOWN — never an editable DRAFT.
 */
function normalizePurchaseInvoiceDraftRaw(raw: unknown, entity = 'purchaseInvoiceDraft'): WorkflowInvoice {
  const record = asRecord(raw);
  if (!record) {
    reportDtoAnomaly(entity, 'root', raw);
  }

  const snapshot = asRecord(record?.sourceSnapshot);
  const seller = asRecord(record?.sellerSnapshot ?? snapshot?.seller);
  const buyer = asRecord(record?.buyerSnapshot ?? snapshot?.buyer);
  const items = asOptionalArray(record?.items, (item, index) =>
    normalizePurchaseInvoiceDraftLine(item, `${entity}.items[${index}]`, record?.currency),
  );

  return {
    id: asOptionalString(record?.id) ?? null,
    version: optionalNullableInteger(record?.version),
    direction: 'PURCHASE',
    netPurchasePrices: record?.netPurchasePrices === true,
    kind: normalizeKind(record?.documentKind, `${entity}.documentKind`),
    status: normalizeServerStatus(record?.status, `${entity}.status`),
    extractionStatus: normalizeServerExtractionStatus(record?.extractionStatus, `${entity}.extractionStatus`),
    extractionError: asOptionalString(record?.extractionError) ?? null,
    extractionModel: asOptionalString(record?.extractionModel) ?? null,
    extractionReview: normalizeExtractionReview(snapshot?.extractionReview, `${entity}.sourceSnapshot.extractionReview`),
    originalDocumentNumber: asOptionalString(record?.documentNumber) ?? null,
    sellerName: asOptionalString(seller?.name) ?? null,
    sellerTaxId: asOptionalString(seller?.taxId) ?? null,
    sellerAddress: asOptionalString(seller?.address) ?? null,
    buyerAddress: asOptionalString(buyer?.address) ?? null,
    orderReference: asOptionalString(snapshot?.orderReference) ?? null,
    paymentTerms: asOptionalString(snapshot?.paymentTerms) ?? null,
    documentNotes: asOptionalString(snapshot?.documentNotes) ?? null,
    buyerName: asOptionalString(buyer?.name) ?? null,
    buyerTaxId: asOptionalString(buyer?.taxId) ?? null,
    issueDate: toDateOnly(record?.issueDate),
    dueDate: toDateOnly(record?.dueDate),
    currency: asOptionalString(record?.currency) ?? null,
    exchangeRateToPLN: optionalNullableNumber(record?.exchangeRate),
    exchangeRateDate: asOptionalString(snapshot?.exchangeRateDate) ?? null,
    exchangeRateSnapshot: normalizeExchangeRateSnapshot(record?.exchangeRateSnapshot),
    totalNet: optionalNullableNumber(record?.subtotalNet),
    totalVat: optionalNullableNumber(record?.vatTotal),
    totalGross: optionalNullableNumber(record?.grossTotal),
    recognizedSupplierOrderId: asOptionalString(record?.recognizedSupplierOrderId) ?? null,
    recognizedOrderId: null,
    recognizedNetPLN: record?.recognizedNetPLN == null ? null : Number(record.recognizedNetPLN),
    settlementPeriod: asOptionalString(record?.recognitionMonth) ?? null,
    supplierOrderId: asOptionalString(record?.supplierOrderId) ?? null,
    supplierId: asOptionalString(record?.supplierId) ?? null,
    orderId: null,
    correctsInvoiceId: asOptionalString(record?.correctsInvoiceId) ?? null,
    coverageState: normalizeCoverageState(record?.coverageState, `${entity}.coverageState`),
    lines: items ?? [],
    linesLoaded: items !== undefined,
    // Server confirms lineage on write; the form still needs an explicit
    // operator confirmation per line before any approval exists.
    manuallyCorrected: record?.manuallyEdited === true,
    approvedAt: toDateOnly(record?.approvedAt),
    approvedBy: asOptionalString(record?.approvedById) ?? null,
    sourceFileName: asOptionalString(record?.sourceFileName) ?? null,
    approvalResult: normalizeApprovalResult(record?.approvalResult),
    sourceFiles: asArray(record?.sourceFiles, (file) => ({ name: asOptionalString(asRecord(file)?.name) ?? '', sha256: asOptionalString(asRecord(file)?.sha256) ?? '' })),
    extractionSeq: optionalNullableInteger(record?.extractionSeq),
    lastAppliedExtractionSeq: optionalNullableInteger(record?.lastAppliedExtractionSeq),
  };
}

function normalizePurchaseInvoiceDraftLine(
  raw: unknown,
  entity: string,
  headerCurrency: unknown,
): WorkflowInvoiceLine {
  const record = asRecord(raw);
  if (!record) {
    reportDtoAnomaly(entity, 'root', raw);
  }
  const id = asOptionalString(record?.id) ?? null;

  return {
    key: id ?? '',
    id,
    lineNumber: optionalNullableInteger(record?.lineNumber),
    productVariantId: asOptionalString(record?.productVariantId) ?? null,
    productName: asOptionalString(record?.productName) ?? null,
    sourceOrderLineId: asOptionalString(record?.sourceSupplierOrderItemId) ?? null,
    matchConfirmed: Boolean(record?.sourceSupplierOrderItemId ?? record?.sourceOrderProductId),
    ...physicalInput(record),
    quantityPcs: optionalNullableNumber(record?.quantityPcs),
    quantityM2: optionalNullableNumber(record?.quantityM2),
    quantityM3: optionalNullableNumber(record?.quantityM3),
    conversionUnit: normalizeLineUnit(record?.conversionUnit, `${entity}.conversionUnit`),
    conversionQuantity: null,
    width: null,
    height: null,
    length: null,
    thickness: null,
    commercialQuantity: optionalNullableNumber(record?.purchaseQuantity),
    commercialUnit: normalizeLineUnit(record?.purchaseUnit, `${entity}.purchaseUnit`),
    unitPrice: optionalNullableNumber(record?.unitPrice),
    taxRate: optionalNullableNumber(record?.vatRate),
    netAmount: optionalNullableNumber(record?.netAmount),
    vatAmount: optionalNullableNumber(record?.vatAmount),
    grossAmount: optionalNullableNumber(record?.grossAmount),
    currency: asOptionalString(headerCurrency) ?? null,
    quantityRaw: optionalNullableNumber(record?.quantityRaw),
    quantityUnitRaw: asOptionalString(record?.quantityUnitRaw) ?? null,
    discountPercent: optionalNullableNumber(record?.discountPercent),
    discountAmount: optionalNullableNumber(record?.discountAmount),
  };
}

/**
 * Adapter for actual sales import reads (`Invoice` + `InvoiceItem`, migration
 * 152). Totals keep the legacy NOT NULL contract: totalAmount feeds
 * totalGross; net/VAT stay unknown unless explicitly carried.
 */
function normalizeSalesInvoiceDraftRaw(raw: unknown, entity = 'salesInvoiceDraft'): WorkflowInvoice {
  const record = asRecord(raw);
  if (!record) {
    reportDtoAnomaly(entity, 'root', raw);
  }

  const snapshot = asRecord(record?.sourceSnapshot);
  const seller = asRecord(record?.seller ?? snapshot?.seller);
  const buyer = asRecord(record?.buyer ?? snapshot?.buyer);
  const items = asOptionalArray(record?.items, (item, index) =>
    normalizeSalesInvoiceDraftLine(item, `${entity}.items[${index}]`, record?.currency),
  );

  return {
    id: asOptionalString(record?.id) ?? null,
    version: optionalNullableInteger(record?.version),
    direction: 'SALES',
    // Sales rows carry legacy `type` (FINAL/PROFORMA/ADVANCE), not documentKind.
    kind: record?.documentKind !== undefined ? normalizeKind(record.documentKind, `${entity}.documentKind`) : normalizeSalesKind(record?.type, `${entity}.type`),
    status: normalizeServerStatus(record?.status, `${entity}.status`),
    extractionStatus: normalizeServerExtractionStatus(record?.extractionStatus, `${entity}.extractionStatus`),
    extractionError: asOptionalString(record?.extractionError) ?? null,
    extractionModel: asOptionalString(record?.extractionModel) ?? null,
    extractionReview: normalizeExtractionReview(snapshot?.extractionReview, `${entity}.sourceSnapshot.extractionReview`),
    originalDocumentNumber: asOptionalString(record?.number ?? record?.documentNumber) ?? null,
    sellerName: asOptionalString(seller?.name) ?? null,
    sellerTaxId: asOptionalString(seller?.taxId) ?? null,
    sellerAddress: asOptionalString(seller?.address) ?? null,
    buyerAddress: asOptionalString(buyer?.address) ?? null,
    orderReference: asOptionalString(snapshot?.orderReference) ?? null,
    paymentTerms: asOptionalString(snapshot?.paymentTerms) ?? null,
    documentNotes: asOptionalString(snapshot?.documentNotes) ?? null,
    buyerName: asOptionalString(buyer?.name) ?? null,
    buyerTaxId: asOptionalString(buyer?.taxId) ?? null,
    issueDate: toDateOnly(record?.issueDate),
    dueDate: toDateOnly(record?.dueDate),
    currency: asOptionalString(record?.currency) ?? null,
    exchangeRateToPLN: optionalNullableNumber(record?.exchangeRate),
    exchangeRateDate: asOptionalString(snapshot?.exchangeRateDate) ?? null,
    exchangeRateSnapshot: normalizeExchangeRateSnapshot(record?.exchangeRateSnapshot),
    totalNet: optionalNullableNumber(record?.subtotalNet),
    totalVat: optionalNullableNumber(record?.vatTotal),
    totalGross: optionalNullableNumber(record?.totalAmount),
    settlementPeriod: asOptionalString(record?.recognitionMonth) ?? null,
    supplierOrderId: null,
    recognizedSupplierOrderId: null,
    recognizedOrderId: asOptionalString(record?.recognizedOrderId) ?? null,
    recognizedNetPLN: record?.recognizedNetPLN == null ? null : Number(record.recognizedNetPLN),
    supplierId: null,
    orderId: asOptionalString(record?.orderId) ?? null,
    correctsInvoiceId: null,
    coverageState: normalizeCoverageState(record?.coverageState, `${entity}.coverageState`),
    lines: items ?? [],
    linesLoaded: items !== undefined,
    manuallyCorrected: record?.manuallyEdited === true,
    approvedAt: toDateOnly(record?.approvedAt),
    approvedBy: asOptionalString(record?.approvedById) ?? null,
    sourceFileName: asOptionalString(record?.sourceFileName) ?? null,
    approvalResult: normalizeApprovalResult(record?.approvalResult),
    sourceFiles: asArray(record?.sourceFiles, (file) => ({ name: asOptionalString(asRecord(file)?.name) ?? '', sha256: asOptionalString(asRecord(file)?.sha256) ?? '' })),
    extractionSeq: optionalNullableInteger(record?.extractionSeq),
    lastAppliedExtractionSeq: optionalNullableInteger(record?.lastAppliedExtractionSeq),
  };
}

function normalizeSalesInvoiceDraftLine(
  raw: unknown,
  entity: string,
  headerCurrency: unknown,
): WorkflowInvoiceLine {
  const record = asRecord(raw);
  if (!record) {
    reportDtoAnomaly(entity, 'root', raw);
  }
  const id = asOptionalString(record?.id) ?? null;

  return {
    key: id ?? '',
    id,
    lineNumber: optionalNullableInteger(record?.lineNumber),
    productVariantId: asOptionalString(record?.productVariantId) ?? null,
    productName: asOptionalString(record?.productName) ?? null,
    sourceOrderLineId: asOptionalString(record?.sourceOrderProductId) ?? null,
    matchConfirmed: Boolean(record?.sourceSupplierOrderItemId ?? record?.sourceOrderProductId),
    ...physicalInput(record),
    quantityPcs: optionalNullableNumber(record?.quantityPcs),
    quantityM2: optionalNullableNumber(record?.quantityM2),
    quantityM3: optionalNullableNumber(record?.quantityM3),
    conversionUnit: normalizeLineUnit(record?.conversionUnit, `${entity}.conversionUnit`),
    conversionQuantity: null,
    width: null,
    height: null,
    length: null,
    thickness: null,
    commercialQuantity: optionalNullableNumber(record?.saleQuantity),
    commercialUnit: normalizeLineUnit(record?.saleUnit, `${entity}.saleUnit`),
    unitPrice: optionalNullableNumber(record?.unitPrice),
    taxRate: optionalNullableNumber(record?.vatRate),
    netAmount: optionalNullableNumber(record?.netAmount),
    vatAmount: optionalNullableNumber(record?.vatAmount),
    grossAmount: optionalNullableNumber(record?.grossAmount),
    currency: asOptionalString(headerCurrency) ?? null,
    quantityRaw: optionalNullableNumber(record?.quantityRaw),
    quantityUnitRaw: asOptionalString(record?.quantityUnitRaw) ?? null,
    discountPercent: optionalNullableNumber(record?.discountPercent),
    discountAmount: optionalNullableNumber(record?.discountAmount),
  };
}

function normalizeDirection(raw: unknown, entity: string): WorkflowInvoiceDirection {
  if (typeof raw === 'string' && (DIRECTIONS as string[]).includes(raw)) {
    return raw as WorkflowInvoiceDirection;
  }
  reportDtoAnomaly(entity, 'direction', raw);
  return 'PURCHASE';
}

function normalizeKind(raw: unknown, entity: string): WorkflowInvoiceKind {
  if (typeof raw === 'string' && (KINDS as string[]).includes(raw)) {
    return raw as WorkflowInvoiceKind;
  }
  reportDtoAnomaly(entity, 'kind', raw);
  return 'UNKNOWN';
}

/** Legacy sales `type` (FINAL/PROFORMA/ADVANCE) onto the shared kind union. */
function normalizeSalesKind(raw: unknown, entity: string): WorkflowInvoiceKind {
  if (raw === 'FINAL') return 'STANDARD';
  if (raw === 'PROFORMA') return 'PROFORMA';
  if (raw === 'ADVANCE') return 'ADVANCE';
  if (raw === undefined || raw === null || raw === '') return 'STANDARD';
  reportDtoAnomaly(entity, 'type', raw);
  return 'UNKNOWN';
}

function normalizeEditorStatus(raw: unknown, entity: string): WorkflowInvoiceStatus {
  if (typeof raw === 'string' && (EDITOR_STATUSES as string[]).includes(raw)) {
    return raw as WorkflowInvoiceStatus;
  }
  reportDtoAnomaly(entity, 'status', raw);
  return 'UNKNOWN';
}

/**
 * Server statuses map closedly: only DRAFT stays editable. APPROVED and
 * CANCELLED are read-only snapshots; anything unrecognized is UNKNOWN
 * (read-only), never an editable DRAFT.
 */
function normalizeServerStatus(raw: unknown, entity: string): WorkflowInvoiceStatus {
  if (raw === 'DRAFT' || raw === 'APPROVED' || raw === 'CANCELLED') {
    return raw;
  }
  reportDtoAnomaly(entity, 'status', raw);
  return 'UNKNOWN';
}

function normalizeExtractionStatus(raw: unknown, entity: string): WorkflowExtractionStatus {
  if (raw === undefined || raw === null || raw === '') return 'IDLE';
  if (typeof raw === 'string' && (EXTRACTION_STATUSES as string[]).includes(raw)) {
    return raw as WorkflowExtractionStatus;
  }
  reportDtoAnomaly(entity, 'extractionStatus', raw);
  return 'IDLE';
}

/** Backend extraction lifecycle: NONE/PENDING/SUCCEEDED/FAILED. */
function normalizeServerExtractionStatus(raw: unknown, entity: string): WorkflowExtractionStatus {
  if (raw === undefined || raw === null || raw === '' || raw === 'NONE') return 'IDLE';
  if (raw === 'PENDING') return 'PENDING';
  if (raw === 'SUCCEEDED') return 'SUCCESS';
  if (raw === 'FAILED') return 'FAILED';
  reportDtoAnomaly(entity, 'extractionStatus', raw);
  return 'IDLE';
}

/**
 * Server-owned original OCR review context (`sourceSnapshot.extractionReview`
 * on server reads, flat `extractionReview` on the internal editor model).
 * Absent stays absent (existing rows may lack the field). `documentTotal` is
 * the printed unspecified TOTAL — never coerced into net/gross — and zero is
 * preserved as a real reading. Only a finite nonnegative number or a numeric
 * nonempty string is accepted; booleans, arrays, objects, negatives and
 * non-finite values collapse to null with an anomaly report (so `true`/`[]`
 * can never surface as apparent money). `currency` is the immutable original
 * OCR currency, never the live editable form currency. Notes are trimmed
 * non-empty strings capped at the backend bound (20 notes, 500 chars each);
 * invalid shapes collapse to empty with an anomaly report.
 */
function normalizeExtractionReview(raw: unknown, entity: string): WorkflowInvoiceExtractionReview | null {
  if (raw === undefined || raw === null) return null;
  const record = asRecord(raw);
  if (!record) {
    reportDtoAnomaly(entity, 'extractionReview', raw);
    return null;
  }
  const documentTotal = normalizeReviewTotal(record.documentTotal, `${entity}.documentTotal`);
  const currency = asOptionalString(record.currency) ?? null;
  let notes: string[] = [];
  const notesRaw = record.notes;
  if (notesRaw === undefined || notesRaw === null) {
    notes = [];
  } else if (Array.isArray(notesRaw)) {
    notes = notesRaw
      .filter((note): note is string => typeof note === 'string')
      .map((note) => note.trim())
      .filter((note) => note.length > 0)
      .slice(0, 20)
      .map((note) => note.slice(0, 500));
  } else {
    reportDtoAnomaly(entity, 'notes', notesRaw);
    notes = [];
  }
  return { documentTotal, currency, notes };
}

/** Printed TOTAL: finite nonnegative number or numeric nonempty string only. */
function normalizeReviewTotal(raw: unknown, entity: string): number | null {
  if (raw === undefined || raw === null) return null;
  if (typeof raw === 'number') {
    if (Number.isFinite(raw) && raw >= 0) return raw;
    reportDtoAnomaly(entity, 'documentTotal', raw);
    return null;
  }
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return null;
    const parsed = Number(trimmed);
    if (Number.isFinite(parsed) && parsed >= 0) return parsed;
    reportDtoAnomaly(entity, 'documentTotal', raw);
    return null;
  }
  reportDtoAnomaly(entity, 'documentTotal', raw);
  return null;
}

function normalizeCoverageState(raw: unknown, entity: string): WorkflowInvoice['coverageState'] {
  if (raw === undefined || raw === null || raw === '') return null;
  if (raw === 'PARTIAL' || raw === 'FULL') return raw;
  reportDtoAnomaly(entity, 'coverageState', raw);
  return null;
}

export function normalizeWorkflowInvoiceLine(raw: unknown, entity = 'workflowInvoiceLine'): WorkflowInvoiceLine {
  const record = asRecord(raw);
  if (!record) {
    reportDtoAnomaly(entity, 'root', raw);
  }
  const key = asOptionalString(record?.key) ?? asOptionalString(record?.id) ?? '';

  return {
    key,
    id: asOptionalString(record?.id) ?? null,
    lineNumber: optionalNullableInteger(record?.lineNumber),
    productVariantId: asOptionalString(record?.productVariantId) ?? null,
    productName: asOptionalString(record?.productName) ?? null,
    sourceOrderLineId: asOptionalString(record?.sourceOrderLineId) ?? null,
    matchConfirmed: record?.matchConfirmed === true,
    unit: normalizeLineUnit(record?.unit, `${entity}.unit`),
    quantity: optionalNullableNumber(record?.quantity),
    quantityPcs: optionalNullableNumber(record?.quantityPcs),
    quantityM2: optionalNullableNumber(record?.quantityM2),
    quantityM3: optionalNullableNumber(record?.quantityM3),
    conversionUnit: normalizeLineUnit(record?.conversionUnit, `${entity}.conversionUnit`),
    conversionQuantity: optionalNullableNumber(record?.conversionQuantity),
    width: optionalNullableNumber(record?.width),
    height: optionalNullableNumber(record?.height),
    length: optionalNullableNumber(record?.length),
    thickness: optionalNullableNumber(record?.thickness),
    commercialQuantity: optionalNullableNumber(
      record?.commercialQuantity ?? record?.purchaseQuantity ?? record?.saleQuantity,
    ),
    commercialUnit: normalizeLineUnit(
      record?.commercialUnit ?? record?.purchaseUnit ?? record?.saleUnit,
      `${entity}.commercialUnit`,
    ),
    unitPrice: optionalNullableNumber(record?.unitPrice),
    taxRate: optionalNullableNumber(record?.taxRate ?? record?.vatRate),
    netAmount: optionalNullableNumber(record?.netAmount),
    vatAmount: optionalNullableNumber(record?.vatAmount),
    grossAmount: optionalNullableNumber(record?.grossAmount),
    currency: asOptionalString(record?.currency) ?? null,
  };
}

function normalizeLineUnit(raw: unknown, entity: string): WorkflowInvoiceLine['unit'] {
  if (raw === undefined || raw === null || raw === '') return null;
  if (raw === 'pcs' || raw === 'm2' || raw === 'm3') return raw;
  // Server free-text units (e.g. legacy "szt.") collapse through the shared
  // vocabulary; anything outside pcs/m2/m3 stays unknown, never relabelled.
  const collapsed = normalizeUnitCode(typeof raw === 'string' ? raw : null);
  if (collapsed === 'pcs' || collapsed === 'm2' || collapsed === 'm3') return collapsed;
  reportDtoAnomaly(entity, 'unit', raw);
  return null;
}

function toDateOnly(value: unknown): string | null {
  if (typeof value !== 'string' || !value) return null;
  return value.length >= 10 ? value.slice(0, 10) : value;
}

function optionalNullableNumber(value: unknown): number | null {
  if (value === undefined || value === null || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function optionalNullableInteger(value: unknown): number | null {
  const parsed = optionalNullableNumber(value);
  return parsed === null ? null : Math.trunc(parsed);
}

function physicalInput(record: Record<string, unknown> | undefined | null): Pick<WorkflowInvoiceLine, 'unit' | 'quantity'> {
  const fields = [['pcs', 'quantityPcs'], ['m2', 'quantityM2'], ['m3', 'quantityM3']] as const;
  const preferred = fields.find(([unit]) => unit === record?.conversionUnit);
  const field = preferred && optionalNullableNumber(record?.[preferred[1]]) !== null ? preferred : fields.find(([, key]) => optionalNullableNumber(record?.[key]) !== null);
  return field ? { unit: field[0], quantity: optionalNullableNumber(record?.[field[1]]) } : { unit: null, quantity: null };
}

function normalizeApprovalResult(raw: unknown): WorkflowInvoice['approvalResult'] {
  const record = asRecord(raw);
  if (!record) return null;
  return { replayed: record.replayed === true, pzDocuments: asArray(record.pzDocuments, rawPz => { const pz = asRecord(rawPz); return { id: asOptionalString(pz?.id) ?? '', pzNumber: asOptionalString(pz?.pzNumber) ?? '', sourceSupplierOrderId: asOptionalString(pz?.sourceSupplierOrderId) ?? '' }; }), orders: asArray(record.orders, rawOrder => { const order = asRecord(rawOrder); return { id: asOptionalString(order?.id) ?? '', commerciallyFrozen: order?.commerciallyFrozen === true, coverageState: order?.coverageState === 'FULL' ? 'FULL' : 'PARTIAL' }; }) };
}

export function normalizePurchaseInvoiceDraft(raw: unknown, entity = 'normalizePurchaseInvoiceDraft'): WorkflowInvoice { return completeInvoiceAmounts(normalizePurchaseInvoiceDraftRaw(raw, entity)); }

export function normalizeSalesInvoiceDraft(raw: unknown, entity = 'normalizeSalesInvoiceDraft'): WorkflowInvoice { return normalizeSalesInvoiceDraftRaw(raw, entity); }

function normalizeExchangeRateSnapshot(raw: unknown): WorkflowInvoice['exchangeRateSnapshot'] {
  const snapshot = asRecord(raw);
  const rate = optionalNullableNumber(snapshot?.rate);
  const source = asOptionalString(snapshot?.source);
  const tableNumber = asOptionalString(snapshot?.tableNumber);
  const requestedDate = asOptionalString(snapshot?.requestedDate);
  const effectiveDate = asOptionalString(snapshot?.effectiveDate);
  if (!rate || rate <= 0 || !source?.startsWith('NBP') || !tableNumber || !requestedDate || !effectiveDate
    || !Number.isFinite(Date.parse(requestedDate)) || !Number.isFinite(Date.parse(effectiveDate))) return null;
  return { rate, source, tableNumber, requestedDate, effectiveDate };
}
